import { useMemo } from 'react';
import cronParser from 'cron-parser';
import cronstrue from 'cronstrue';

/**
 * Options for the useCronValidation hook
 */
export interface UseCronValidationOptions {
  expression: string;
  timezone?: string; // default 'UTC'
}

/**
 * Return type for the useCronValidation hook
 */
export interface UseCronValidationReturn {
  isValid: boolean;
  error: string | null;
  nextExecutions: Date[];
  humanReadable: string;
}

/**
 * Converts AWS EventBridge cron syntax (6 fields) to standard cron syntax (5 fields)
 * EventBridge format: minute hour day-of-month month day-of-week year
 * Standard format: minute hour day-of-month month day-of-week
 * 
 * EventBridge uses '?' for day-of-month or day-of-week when the other is specified
 * Standard cron doesn't support '?', so we convert it to '*'
 * 
 * @param expression - AWS EventBridge cron expression (6 fields)
 * @returns Standard cron expression (5 fields) or null if invalid format
 */
export const convertEventBridgeToStandardCron = (expression: string): string | null => {
  if (!expression || typeof expression !== 'string') {
    return null;
  }

  const trimmed = expression.trim();
  const fields = trimmed.split(/\s+/);

  // EventBridge cron has 6 fields (including year)
  if (fields.length !== 6) {
    return null;
  }

  // Remove the year field (last field) for standard cron
  const standardFields = fields.slice(0, 5);

  // Convert '?' to '*' since standard cron doesn't support '?'
  // In EventBridge, '?' means "no specific value" for day-of-month or day-of-week
  const convertedFields = standardFields.map((field) => {
    if (field === '?') {
      return '*';
    }
    return field;
  });

  return convertedFields.join(' ');
};

/**
 * Validates the cross-field rule for day-of-month and day-of-week
 * AWS EventBridge requires that if one is specified (not '?'), the other must be '?'
 * 
 * @param dayOfMonth - The day-of-month field value
 * @param dayOfWeek - The day-of-week field value
 * @returns Object with isValid boolean and optional error message
 */
export const validateDayOfMonthAndDayOfWeek = (
  dayOfMonth: string,
  dayOfWeek: string
): { isValid: boolean; error?: string } => {
  const isDayOfMonthSpecified = dayOfMonth !== '?' && dayOfMonth !== '*';
  const isDayOfWeekSpecified = dayOfWeek !== '?' && dayOfWeek !== '*';

  // Both cannot be specified at the same time
  if (isDayOfMonthSpecified && isDayOfWeekSpecified) {
    return {
      isValid: false,
      error: 'Cannot specify both day-of-month and day-of-week. One must be "?" when the other is specified.',
    };
  }

  // At least one should be specified (not both '?')
  // Note: Both being '*' is technically valid but unusual
  if (dayOfMonth === '?' && dayOfWeek === '?') {
    return {
      isValid: false,
      error: 'Either day-of-month or day-of-week must be specified. Both cannot be "?".',
    };
  }

  return { isValid: true };
};

/**
 * Validates if a string is a valid AWS EventBridge cron expression
 * EventBridge cron format: minute hour day-of-month month day-of-week year
 * 
 * @param expression - The cron expression to validate
 * @returns true if valid EventBridge cron syntax, false otherwise
 */
export const isValidEventBridgeCronFormat = (expression: string): boolean => {
  if (!expression || typeof expression !== 'string') {
    return false;
  }

  const trimmed = expression.trim();
  const fields = trimmed.split(/\s+/);

  // Must have exactly 6 fields
  if (fields.length !== 6) {
    return false;
  }

  // Basic validation for each field
  // Minute: 0-59, *, or cron patterns
  // Hour: 0-23, *, or cron patterns
  // Day-of-month: 1-31, *, ?, L, W, or cron patterns
  // Month: 1-12, JAN-DEC, *, or cron patterns
  // Day-of-week: 1-7, SUN-SAT, *, ?, L, #, or cron patterns
  // Year: 1970-2199, *, or cron patterns

  const fieldPatterns = [
    /^(\*|[0-5]?\d)(\/\d+)?$|^(\*|[0-5]?\d)(-[0-5]?\d)?(,(\*|[0-5]?\d)(-[0-5]?\d)?)*$/, // minute
    /^(\*|[01]?\d|2[0-3])(\/\d+)?$|^(\*|[01]?\d|2[0-3])(-([01]?\d|2[0-3]))?(,(\*|[01]?\d|2[0-3])(-([01]?\d|2[0-3]))?)*$/, // hour
    /^(\*|\?|[1-9]|[12]\d|3[01]|L|W)(\/\d+)?$|^(\*|\?|[1-9]|[12]\d|3[01])(-([1-9]|[12]\d|3[01]))?(,(\*|\?|[1-9]|[12]\d|3[01])(-([1-9]|[12]\d|3[01]))?)*$/, // day-of-month
    /^(\*|[1-9]|1[0-2]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)(\/\d+)?$|^(\*|[1-9]|1[0-2]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)(-([1-9]|1[0-2]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC))?(,(\*|[1-9]|1[0-2]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)(-([1-9]|1[0-2]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC))?)*$/i, // month
    /^(\*|\?|[1-7]|SUN|MON|TUE|WED|THU|FRI|SAT|L)(\/\d+)?$|^(\*|\?|[1-7]|SUN|MON|TUE|WED|THU|FRI|SAT)(#[1-5])?(-([1-7]|SUN|MON|TUE|WED|THU|FRI|SAT))?(,(\*|\?|[1-7]|SUN|MON|TUE|WED|THU|FRI|SAT)(#[1-5])?(-([1-7]|SUN|MON|TUE|WED|THU|FRI|SAT))?)*$/i, // day-of-week
    /^(\*|\d{4})(\/\d+)?$|^(\*|\d{4})(-\d{4})?(,(\*|\d{4})(-\d{4})?)*$/, // year
  ];

  for (let i = 0; i < fields.length; i++) {
    if (!fieldPatterns[i].test(fields[i])) {
      return false;
    }
  }

  return true;
};

/**
 * Validates EventBridge cron format including cross-field rules
 * Returns detailed error information for better user feedback
 * 
 * @param expression - The cron expression to validate
 * @returns Object with isValid boolean and optional error message
 */
export const validateEventBridgeCron = (
  expression: string
): { isValid: boolean; error?: string } => {
  if (!expression || typeof expression !== 'string') {
    return { isValid: false, error: 'Cron expression is required' };
  }

  const trimmed = expression.trim();
  const fields = trimmed.split(/\s+/);

  // Must have exactly 6 fields
  if (fields.length !== 6) {
    return {
      isValid: false,
      error: `Invalid cron expression: expected 6 fields (minute hour day-of-month month day-of-week year), got ${fields.length}`,
    };
  }

  // Basic format validation
  if (!isValidEventBridgeCronFormat(trimmed)) {
    return {
      isValid: false,
      error: 'Invalid cron expression: one or more fields contain invalid values',
    };
  }

  // Cross-field validation for day-of-month and day-of-week
  const dayOfMonth = fields[2];
  const dayOfWeek = fields[4];
  const crossFieldValidation = validateDayOfMonthAndDayOfWeek(dayOfMonth, dayOfWeek);
  
  if (!crossFieldValidation.isValid) {
    return crossFieldValidation;
  }

  return { isValid: true };
};

/**
 * Custom hook for validating cron expressions and providing execution previews
 * 
 * Handles AWS EventBridge cron syntax (6 fields: minute hour day-of-month month day-of-week year)
 * and provides:
 * - Validation status
 * - Error messages for invalid expressions
 * - Next 5 execution times
 * - Human-readable description of the schedule
 * 
 * @param options - Configuration options including the cron expression and timezone
 * @returns Validation result with isValid, error, nextExecutions, and humanReadable
 * 
 * @example
 * ```tsx
 * const { isValid, error, nextExecutions, humanReadable } = useCronValidation({
 *   expression: '0 12 * * ? *',
 *   timezone: 'UTC'
 * });
 * ```
 * 
 * Requirements: 2.3, 8.1, 8.7
 */
export const useCronValidation = (options: UseCronValidationOptions): UseCronValidationReturn => {
  const { expression, timezone = 'UTC' } = options;

  return useMemo(() => {
    // Handle empty expression
    if (!expression || expression.trim() === '') {
      return {
        isValid: false,
        error: 'Cron expression is required',
        nextExecutions: [],
        humanReadable: '',
      };
    }

    const trimmedExpression = expression.trim();

    // Validate EventBridge format including cross-field rules
    const validation = validateEventBridgeCron(trimmedExpression);
    if (!validation.isValid) {
      return {
        isValid: false,
        error: validation.error || 'Invalid cron expression',
        nextExecutions: [],
        humanReadable: '',
      };
    }

    // Convert to standard cron for parsing
    const standardCron = convertEventBridgeToStandardCron(trimmedExpression);
    if (!standardCron) {
      return {
        isValid: false,
        error: 'Invalid cron expression format',
        nextExecutions: [],
        humanReadable: '',
      };
    }

    try {
      // Parse the cron expression to get next execution times
      const interval = cronParser.parseExpression(standardCron, {
        tz: timezone,
        currentDate: new Date(),
      });

      // Validate minimum interval: schedules must not run more frequently than once per day (1440 minutes)
      const MIN_INTERVAL_MINUTES = 1440; // 24 hours
      try {
        const checkInterval = cronParser.parseExpression(standardCron, {
          tz: timezone,
          currentDate: new Date(),
        });
        const first = checkInterval.next().toDate();
        const second = checkInterval.next().toDate();
        const gapMinutes = (second.getTime() - first.getTime()) / (1000 * 60);
        if (gapMinutes < MIN_INTERVAL_MINUTES) {
          return {
            isValid: false,
            error: `Schedule interval (${Math.round(gapMinutes)} minutes) is too frequent. Schedules must run at most once per day (minimum ${MIN_INTERVAL_MINUTES} minutes between executions).`,
            nextExecutions: [],
            humanReadable: '',
          };
        }
      } catch {
        // If we can't compute the interval, let other validation catch it
      }

      // Get next 5 execution times
      const nextExecutions: Date[] = [];
      for (let i = 0; i < 5; i++) {
        try {
          const next = interval.next();
          nextExecutions.push(next.toDate());
        } catch {
          // No more iterations available
          break;
        }
      }

      // Generate human-readable description using cronstrue
      // Use the standard 5-field cron for cronstrue
      let humanReadable = '';
      try {
        humanReadable = cronstrue.toString(standardCron, {
          throwExceptionOnParseError: false,
          use24HourTimeFormat: true,
        });
      } catch {
        // If cronstrue fails, provide a basic description
        humanReadable = 'Custom schedule';
      }

      return {
        isValid: true,
        error: null,
        nextExecutions,
        humanReadable,
      };
    } catch (parseError) {
      // Handle parsing errors from cron-parser
      const errorMessage = parseError instanceof Error 
        ? parseError.message 
        : 'Invalid cron expression';
      
      return {
        isValid: false,
        error: `Invalid cron expression: ${errorMessage}`,
        nextExecutions: [],
        humanReadable: '',
      };
    }
  }, [expression, timezone]);
};

export default useCronValidation;
